<template>
  <section>
    <div class="titulo">
      <div class="margem container">
        <button type="button" class="link-voltar" @click="voltarAnterior">
          <i class="fa-solid fa-arrow-left"></i> Voltar à tela anterior
        </button>
        <h2>Dados completos da visita</h2>
      </div>
    </div>

    <div class="margem container">
      <div v-if="carregando" class="bloco margem alinha-centro">
        <i class="fas fa-spinner fa-spin"></i> Carregando...
      </div>
      <div v-else-if="!dados" class="bloco margem alinha-centro">
        Solicitação não encontrada.
      </div>
      <div v-else-if="!permissoes.includes(256)" class="bloco margem card-dados">
        <div class="secao">
          <h3 class="secao-titulo"><i class="fa-solid fa-user-tie"></i> Anfitrião(ã)</h3>
          <dl class="lista-dados">
            <dt>Nome</dt>
            <dd>{{ dados.nomeAnfitriao || "—" }}</dd>
          </dl>
        </div>
        <div class="secao">
          <h3 class="secao-titulo">
            <i class="fa-solid fa-people-group"></i> Visitantes desta visita
            <span v-if="dados.ehVisitaEmGrupo" class="badge-grupo">({{ dados.listaVisitantes.length }} pessoa(s))</span>
          </h3>
          <ul class="lista-nomes">
            <li v-for="(v, idx) in dados.listaVisitantes" :key="idx">{{ v.nomeCompleto || "—" }}</li>
          </ul>
        </div>
        <div class="secao">
          <h3 class="secao-titulo"><i class="fa-solid fa-building"></i> Empresa</h3>
          <dl class="lista-dados">
            <dt>Empresa de origem</dt>
            <dd>{{ dados.empresaOrigem || "—" }}</dd>
          </dl>
        </div>
        <div class="secao">
          <h3 class="secao-titulo">Status do agendamento</h3>
          <span class="badge-status" :class="classeStatus(dados.statusAgendamento)">{{ textoStatus(dados.statusAgendamento) }}</span>
        </div>
        <div class="acoes">
          <button type="button" class="btn-voltar" @click="voltarAnterior">
            <i class="fa-solid fa-arrow-left"></i> Voltar à tela anterior
          </button>
        </div>
      </div>
      <div v-else class="bloco margem card-dados">
        <!-- Anfitrião(ã) -->
        <div class="secao">
          <h3 class="secao-titulo"><i class="fa-solid fa-user-tie"></i> Anfitrião(ã) (Solicitante)</h3>
          <dl class="lista-dados">
            <dt>Agendada</dt>
            <dd>{{ dados.agendada ? "Sim" : "Não" }}</dd>
            <dt>Nome</dt>
            <dd>{{ dados.nomeAnfitriao || "—" }}</dd>
            <dt>Área/Departamento</dt>
            <dd>{{ dados.areaDepartamento || "—" }}</dd>
            <dt>Data da solicitação</dt>
            <dd>{{ formatarData(dados.dataSolicitacao) }}</dd>
          </dl>
        </div>

        <!-- Lista de visitantes (só nomes) -->
        <div class="secao">
          <h3 class="secao-titulo">
            <i class="fa-solid fa-people-group"></i> Visitantes desta visita
            <span v-if="dados.ehVisitaEmGrupo" class="badge-grupo">({{ dados.listaVisitantes.length }} pessoa(s))</span>
          </h3>
          <ul class="lista-nomes">
            <li v-for="(v, idx) in dados.listaVisitantes" :key="idx">{{ v.nomeCompleto || "—" }}</li>
          </ul>
          <router-link v-if="idVisita" :to="'/acesso/visita/' + idVisita + '/dados-completos'" class="link-secundario">
            <i class="fa-solid fa-user"></i> Ver dados completos de cada visitante
          </router-link>
        </div>

        <!-- Fluxo digital (anfitrião; exigido para cliente/parceiro, dispensado para os demais tipos) -->
        <div v-if="dados.origem === 'anfitriao' && !isentaFluxoDigitalRecepcao(dados)" class="secao secao-fluxo-convite">
          <h3 class="secao-titulo"><i class="fa-solid fa-envelope-open-text"></i> Fluxo digital da visita</h3>
          <p class="fluxo-convite-desc">Quatro etapas conduzidas pela recepção: e-mail ao visitante, confirmação de presença, agendamento de sala e encaminhamento logístico.</p>
          <ul class="fluxo-convite-lista">
            <li v-for="(it, idx) in etapasFluxoResumo" :key="idx" :class="{ feito: it.ts }">
              <i class="fa-solid fa-check" v-if="it.ts"></i>
              <i class="fa-solid fa-circle" v-else></i>
              {{ it.label }} — {{ it.ts ? formatarDataHora(it.ts) : "Pendente" }}
            </li>
          </ul>
          <p v-if="dados.conviteCompleto" class="convite-completo-msg"><i class="fa-solid fa-circle-check"></i> Fluxo digital concluído — visita pode ser agendada.</p>
        </div>
        <div v-else-if="dados.origem === 'anfitriao' && isentaFluxoDigitalRecepcao(dados)" class="secao">
          <h3 class="secao-titulo"><i class="fa-solid fa-handshake"></i> Fluxo da recepção</h3>
          <p class="fluxo-convite-desc fonte-menor fonte-fraca">
            Visita de <strong>fornecedor</strong>, <strong>prestador de serviço</strong> ou <strong>outros</strong>: e-mail ao visitante, confirmação de presença,
            agendamento de sala e encaminhamento logístico <strong>não são exigidos</strong>.
          </p>
        </div>

        <!-- Tipo de visitante -->
        <div class="secao">
          <h3 class="secao-titulo"><i class="fa-solid fa-handshake"></i> Tipo de visitante</h3>
          <div class="tipos">
            <span v-if="dados.tipoCliente" class="badge-tipo">Cliente</span>
            <span v-if="dados.tipoFornecedor" class="badge-tipo">Fornecedor</span>
            <span v-if="dados.tipoPrestadorServico" class="badge-tipo">Prestador de serviço</span>
            <span v-if="dados.tipoParceiros" class="badge-tipo">Parceiros</span>
            <span v-if="dados.tipoOutros" class="badge-tipo">Outros: {{ dados.tipoOutrosDescricao || "—" }}</span>
            <span v-if="!dados.tipoCliente && !dados.tipoFornecedor && !dados.tipoPrestadorServico && !dados.tipoParceiros && !dados.tipoOutros">—</span>
          </div>
        </div>

        <!-- Informações da visita -->
        <div class="secao">
          <h3 class="secao-titulo"><i class="fa-solid fa-calendar-day"></i> Informações da visita</h3>
          <dl class="lista-dados">
            <dt>Empresa de origem</dt>
            <dd>{{ dados.empresaOrigem || "—" }}</dd>
            <dt>Motivo</dt>
            <dd>{{ dados.motivoVisita || "—" }}</dd>
            <dt>Área/setor a visitar</dt>
            <dd>{{ dados.areaSetorVisitado || "—" }}</dd>
            <dt>{{ dados.ehVisitaPorPeriodo ? 'Período da visita' : 'Data da visita' }}</dt>
            <dd>{{ textoPeriodoVisita(dados) }}</dd>
            <dt>Horários previstos (entrada / saída)</dt>
            <dd>{{ dados.horarioEntradaPrevisto || "—" }} / {{ dados.horarioSaidaPrevisto || "—" }}</dd>
            <dt>Horários efetivos (check-in / check-out)</dt>
            <dd>{{ textoHorariosEfetivos(dados) }}</dd>
            <dt>Quantidade de visitantes</dt>
            <dd>{{ dados.quantidadeVisitantes ?? dados.listaVisitantes?.length ?? "—" }}</dd>
          </dl>
        </div>

        <!-- Necessidades logísticas -->
        <div class="secao">
          <h3 class="secao-titulo"><i class="fa-solid fa-industry"></i> Necessidades logísticas</h3>
          <div class="tipos">
            <span v-if="dados.visitaraFabrica" class="badge-tipo">Visitará à fábrica</span>
            <span v-if="dados.salaReuniaoLite" class="badge-tipo">Sala de reunião Lite</span>
            <span v-if="dados.salaReuniaoHard" class="badge-tipo">Sala de reunião Hard</span>
            <span v-if="dados.cafe" class="badge-tipo">Café</span>
            <span v-if="dados.agua" class="badge-tipo">Água</span>
            <span v-if="dados.paoQueijoBiscoito" class="badge-tipo">Pão de queijo ou biscoito</span>
            <span v-if="dados.brinde" class="badge-tipo">Brinde</span>
            <span v-if="!dados.visitaraFabrica && !dados.salaReuniaoLite && !dados.salaReuniaoHard && !dados.cafe && !dados.agua && !dados.paoQueijoBiscoito && !dados.brinde">—</span>
          </div>
        </div>

        <!-- Status -->
        <div class="secao">
          <h3 class="secao-titulo">Status do agendamento</h3>
          <span class="badge-status" :class="classeStatus(dados.statusAgendamento)">{{ textoStatus(dados.statusAgendamento) }}</span>
        </div>

        <div class="acoes">
          <router-link v-if="idVisita" :to="{ name: 'CheckListAcesso', params: { id: String(idVisita) } }"
            class="btn-ir-checklist">
            <i class="fa-solid fa-clipboard-check"></i> Check-list desta visita
          </router-link>
          <button type="button" class="btn-voltar" @click="voltarAnterior">
            <i class="fa-solid fa-arrow-left"></i> Voltar à tela anterior
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { createToaster } from "@meforma/vue-toaster";
import solicitacaoVisitaService from "@/services/solicitacao-visita-service";
import { getPermissao } from "@/services/permissaoService";

import { textoStatus as textoStatusConfig, classeStatus as classeStatusConfig } from "@/config/status-visita";
import { formatarPeriodoVisitaPtBr } from "@/utils/visita-data";
import { isentaFluxoDigitalRecepcao } from "@/config/fluxo-digital-solicitacao";

const toaster = createToaster({ position: "top-right" });

export default {
  name: "DadosCompletosVisitaView",

  data() {
    return {
      dados: null,
      carregando: true,
      permissoes: [],
    };
  },

  computed: {
    idVisita() {
      return this.$route.params.id || null;
    },
    /** Destino se não houver histórico para router.back() (ex.: abertura direta do link). */
    voltarFallbackUrl() {
      if (this.$route.query.from === "agenda") return "/";
      if (this.$route.query.from === "checkin-dia") {
        const d = this.$route.query.data;
        if (d && /^\d{4}-\d{2}-\d{2}$/.test(String(d))) {
          return { path: "/acesso/checkin-dia", query: { data: String(d).slice(0, 10) } };
        }
        return "/acesso/checkin-dia";
      }
      return this.idVisita ? `/acesso/checklist/${this.idVisita}` : "/";
    },
    etapasFluxoResumo() {
      const d = this.dados;
      if (!d) return [];
      return [
        { label: "Envio de e-mail ao visitante", ts: d.conviteEnviadoEm },
        {
          label: "Confirmação de presença do visitante",
          ts: d.confirmacaoPresencaVisitanteEm ?? d.informacoesPreenchidasEm,
        },
        {
          label: "Agendamento de sala",
          ts: d.agendamentoSalaEfetuadoEm ?? d.documentoConfidencialidadeRecebidoEm,
        },
        { label: "Encaminhamento das necessidades logísticas", ts: d.necessidadesLogisticasEncaminhadasEm },
      ];
    },
  },

  async created() {
    this.permissoes = await getPermissao();
    this.carregar();
  },

  methods: {
    isentaFluxoDigitalRecepcao,

    voltarAnterior() {
      if (typeof window !== "undefined" && window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push(this.voltarFallbackUrl);
    },
    formatarData(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString("pt-BR");
    },
    textoPeriodoVisita(dados) {
      if (!dados) return "—";
      const s = formatarPeriodoVisitaPtBr(dados.dataVisita, dados.dataVisitaFim);
      return s || "—";
    },
    formatarDataHora(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    horaLocalDeIso(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
    },
    textoHorariosEfetivos(dados) {
      if (!dados) return "—";
      const e = this.horaLocalDeIso(dados.checkinEm);
      const s = this.horaLocalDeIso(dados.checkoutEm);
      if (!e && !s) return "—";
      return `${e || "—"} / ${s || "—"}`;
    },
    textoStatus(s) {
      return textoStatusConfig(s);
    },
    classeStatus(s) {
      return classeStatusConfig(s);
    },
    async carregar() {
      if (!this.idVisita) {
        this.carregando = false;
        return;
      }
      this.carregando = true;
      try {
        this.dados = await solicitacaoVisitaService.obterPorId(this.idVisita);
      } catch (err) {
        console.error(err);
        toaster.show("Erro ao carregar dados.", { type: "error" });
      } finally {
        this.carregando = false;
      }
    },
  },
};
</script>

<style scoped>
.link-voltar {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--cor-primaria-forte);
  text-decoration: none;
  font-size: 0.95rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}
.link-voltar:hover { text-decoration: underline; }
.card-dados {
  max-width: 720px;
  border-radius: 12px;
  border: 1px solid var(--cor-separador);
  padding: 1.5rem;
}
.secao {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--cor-separador);
}
.secao:last-of-type { border-bottom: none; }
.secao-titulo { font-size: 1rem; margin-bottom: 0.75rem; color: var(--cor-fonte-forte); }
.secao-titulo i { margin-right: 0.5rem; }
.badge-grupo { font-size: 0.8rem; color: var(--cor-primaria-forte); margin-left: 0.5rem; font-weight: normal; }
.lista-dados { display: grid; grid-template-columns: 180px 1fr; gap: 0.35rem 1rem; margin: 0; font-size: 0.95rem; }
.lista-dados dt { color: var(--cor-fonte-fraca); font-weight: 500; }
.lista-dados dd { margin: 0; }
.lista-nomes { margin: 0 0 0.75rem 0; padding-left: 1.25rem; font-size: 0.95rem; }
.lista-nomes li { margin-bottom: 0.25rem; }
.secao-fluxo-convite {
  background: var(--cor-cinza);
  border-radius: 6px;
  padding: 1rem 1.25rem;
}
.fluxo-convite-desc { font-size: 0.9rem; color: var(--cor-fonte-fraca); margin: 0 0 0.75rem 0; }
.fluxo-convite-lista { list-style: none; margin: 0; padding: 0; }
.fluxo-convite-lista li { display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0; font-size: 0.95rem; }
.fluxo-convite-lista li i.fa-check { color: var(--cor-sucesso); }
.fluxo-convite-lista li i.fa-circle { color: var(--cor-fonte-fraca); font-size: 0.5rem; }
.fluxo-convite-lista li.feito { color: var(--cor-fonte-forte); }
.convite-completo-msg { margin: 0.75rem 0 0 0; padding: 0.5rem 0; color: var(--cor-sucesso); font-size: 0.95rem; display: flex; align-items: center; gap: 0.5rem; }
.link-secundario { display: inline-flex; align-items: center; gap: 0.5rem; color: var(--cor-primaria-forte); text-decoration: none; font-size: 0.95rem; }
.link-secundario:hover { text-decoration: underline; }
.tipos { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.badge-tipo { padding: 0.25rem 0.5rem; background: var(--cor-primaria-fraca); color: var(--cor-primaria-forte); border-radius: 6px; font-size: 0.9rem; }
.badge-status { padding: 0.35rem 0.75rem; border-radius: 6px; font-size: 0.95rem; }
.status-pendente { background: var(--cor-cinza); color: var(--cor-fonte-fraca); }
.status-processo { background: var(--cor-primaria-fraca); color: var(--cor-primaria-forte); }
.status-confirmada { background: var(--cor-primaria-fraca); color: var(--cor-primaria-forte); }
.status-em-visita { background: var(--cor-primaria-fraca); color: var(--cor-primaria-forte); }
.status-concluido { background: var(--cor-primaria-fraca); color: var(--cor-sucesso); }
.acoes {
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}
.btn-ir-checklist {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--cor-primaria);
  color: var(--cor-bg);
  border-radius: 24px;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 700;
}
.btn-ir-checklist:hover {
  background: var(--cor-primaria-forte);
  color: var(--cor-bg);
}
.btn-voltar {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--cor-primaria);
  color: var(--cor-bg);
  border: none;
  border-radius: 24px;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}
.btn-voltar:hover { background: var(--cor-primaria-forte); color: var(--cor-bg); }
</style>
